import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/Card';
import { Chip } from '../components/Chip';
import { Nav } from '../components/Nav';
import { LoadingState } from '../components/LoadingState';
import { APIError, api, type ComplianceBreakdown, type WeeklyReview } from '../lib/api';

export default function ReviewScreen() {
  const [review, setReview] = useState<WeeklyReview | null>(null);
  const [history, setHistory] = useState<WeeklyReview[]>([]);
  const [notesDraft, setNotesDraft] = useState('');
  const [notesStatus, setNotesStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const lastSavedNotes = useRef<string>('');

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const r = await api.getCurrentReview();
      setReview(r);
      const initialNotes = r.user_notes ?? '';
      setNotesDraft(initialNotes);
      lastSavedNotes.current = initialNotes;
      try {
        const list = await api.listReviews();
        setHistory(list ?? []);
      } catch {
        setHistory([]);
      }
    } catch (e) {
      if (e instanceof APIError && e.status === 404) {
        setError('No review yet. Week 1 starts Monday.');
      } else {
        setError(String(e));
      }
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const saveNotesIfChanged = async () => {
    if (!review) return;
    if (notesDraft === lastSavedNotes.current) return;
    setNotesStatus('saving');
    try {
      await api.patchReviewNotes(review.week_number, notesDraft);
      lastSavedNotes.current = notesDraft;
      setNotesStatus('saved');
      setTimeout(() => setNotesStatus('idle'), 1500);
    } catch (e) {
      setNotesStatus('error');
      setError(String(e));
    }
  };

  const recompute = async () => {
    if (!review) return;
    setBusy(true);
    try {
      setReview(await api.runReview(review.week_number));
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  const acknowledge = async () => {
    if (!review) return;
    setBusy(true);
    try {
      await api.ackReview(review.week_number);
      await refresh();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  if (!review) {
    return (
      <>
        <div className="min-h-full max-w-md mx-auto px-5 py-12 space-y-3 pb-24">
          <p className="label-eyebrow">Gios Protocol</p>
          <h1 className="font-display text-5xl leading-none">Weekly review</h1>
          {error ? (
            <p className="text-sm text-fg-muted leading-relaxed">{error}</p>
          ) : (
            <LoadingState />
          )}
          <Link to="/dashboard" className="font-mono text-[10px] uppercase tracking-[0.2em] text-fg-muted hover:text-fg">
            ← Dashboard
          </Link>
        </div>
        <Nav />
      </>
    );
  }

  const acked = !!review.user_acknowledged_at;
  return (
    <>
      <div className="min-h-full max-w-md lg:max-w-5xl mx-auto px-5 lg:px-8 py-10 lg:py-12 space-y-6 pb-32 stack-2col">
        <header className="space-y-3">
          <Link to="/dashboard" className="font-mono text-[10px] uppercase tracking-[0.2em] text-fg-faint hover:text-fg">
            ← Dashboard
          </Link>
          <div className="flex items-end gap-3">
            <p className="font-display text-7xl leading-none tabular-nums">
              W<span className="text-fg-muted">/</span>{review.week_number}
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Chip tone="muted">Monday {review.monday_date}</Chip>
            <Chip tone="muted">Block {review.block}</Chip>
          </div>
        </header>

        <Card eyebrow="Weight">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="label-eyebrow">Target</div>
              <div className="font-display text-3xl tabular-nums mt-1">
                {review.target_weight_kg.toFixed(1)}
                <span className="text-fg-muted text-base align-top ml-1">kg</span>
              </div>
            </div>
            <div>
              <div className="label-eyebrow">Logged</div>
              <div className="font-display text-3xl tabular-nums mt-1">
                {review.weight_kg !== null ? review.weight_kg.toFixed(1) : '—'}
                {review.weight_kg !== null && (
                  <span className="text-fg-muted text-base align-top ml-1">kg</span>
                )}
              </div>
            </div>
          </div>
          {review.delta_from_target_kg !== null && (
            <Chip tone={review.delta_from_target_kg <= 0 ? 'good' : 'warn'} dot>
              {review.delta_from_target_kg > 0 ? '+' : ''}
              {review.delta_from_target_kg.toFixed(2)} kg vs target
            </Chip>
          )}
          {review.trailing_4wk_avg_loss_kg !== null && (
            <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-fg-faint">
              4-wk avg{' '}
              <span className="text-fg normal-case tracking-normal">
                {review.trailing_4wk_avg_loss_kg.toFixed(2)} kg/wk
              </span>
            </p>
          )}
        </Card>

        <Card eyebrow="Compliance" hint={`${review.compliance_score_pct.toFixed(0)} / 100`}>
          {review.compliance_breakdown && <ComplianceBars b={review.compliance_breakdown} />}
        </Card>

        <Card eyebrow="Action" hint={review.course_correction_action}>
          <p className="text-sm text-fg leading-relaxed">{review.action_message ?? '—'}</p>
        </Card>

        <Card
          eyebrow="Journal"
          hint="How did this week feel? Energy, hunger, sleep, training quality. Saved on blur."
          action={
            notesStatus === 'saving' ? (
              <Chip tone="muted">Saving…</Chip>
            ) : notesStatus === 'saved' ? (
              <Chip tone="good" dot>Saved</Chip>
            ) : notesStatus === 'error' ? (
              <Chip tone="danger" dot>Failed</Chip>
            ) : null
          }
        >
          <textarea
            value={notesDraft}
            onChange={(e) => setNotesDraft(e.target.value)}
            onBlur={saveNotesIfChanged}
            rows={4}
            maxLength={4000}
            placeholder="Energy 7/10. Hunger spiked Wed-Thu. Knees feel fine after the activation block. Need more sleep on Sat — late client call."
            className="w-full rounded-control bg-bg-sunken border border-line px-3 py-2.5 text-sm text-fg placeholder:text-fg-faint resize-none transition-colors focus:outline-none focus:border-accent focus:bg-bg-raised"
          />
        </Card>

        {history.length > 1 && (
          <Card eyebrow="Past weeks" hint="Most recent 4. Tap to remember.">
            <ul className="space-y-2">
              {history
                .filter((r) => r.week_number !== review.week_number)
                .slice(0, 4)
                .map((r) => (
                  <li
                    key={r.week_number}
                    className="rounded-panel border border-line p-3 space-y-1"
                  >
                    <div className="flex items-baseline justify-between">
                      <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-muted">
                        Week {r.week_number} · {r.monday_date}
                      </span>
                      {r.weight_kg !== null && (
                        <span className="font-mono text-xs tabular-nums text-fg">
                          {r.weight_kg.toFixed(1)} kg
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-fg-muted leading-relaxed line-clamp-3">
                      {r.user_notes && r.user_notes.length > 0
                        ? r.user_notes
                        : <span className="text-fg-faint italic">No notes</span>}
                    </p>
                  </li>
                ))}
            </ul>
          </Card>
        )}

        <div className="flex gap-2">
          <button onClick={recompute} disabled={busy} className="btn-secondary px-5 py-3 text-sm">
            Recompute
          </button>
          <button
            onClick={acknowledge}
            disabled={busy || acked}
            className={
              // Acknowledged is a settled fact — the completion pastel, not a
              // dimmed-out secondary button.
              acked
                ? 'flex-1 rounded-full border border-good/25 bg-good-soft py-3 text-sm font-semibold text-good'
                : 'btn-primary flex-1 py-3 text-sm'
            }
          >
            {acked
              ? `✓ Acknowledged ${fmtTime(review.user_acknowledged_at)}`
              : 'Acknowledge & continue'}
          </button>
        </div>

        {error && <p className="text-xs text-danger">{error}</p>}
      </div>
      <Nav />
    </>
  );
}

function ComplianceBars({ b }: { b: ComplianceBreakdown }) {
  const items: Array<[string, number, number]> = [
    ['Meals on time', b.meals_pct, 25],
    ['Steps target', b.steps_pct, 20],
    ['Training', b.training_pct, 20],
    ['Sleep ≥ 7h', b.sleep_pct, 15],
    ['Mobility', b.mobility_pct, 10],
    ['Zero alcohol', b.alcohol_pct, 10],
  ];
  return (
    <ul className="space-y-3">
      {items.map(([label, val, max]) => {
        const pct = Math.min(100, (val / max) * 100);
        return (
          <li key={label} className="space-y-1.5">
            <div className="flex justify-between font-mono text-[10px] uppercase tracking-[0.16em]">
              <span className="text-fg-muted">{label}</span>
              <span className="text-fg tabular-nums">
                {val.toFixed(0)} / {max}
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-bg-sunken overflow-hidden">
              <div
                className="h-full rounded-full bg-good-mid transition-[width] duration-500"
                style={{ width: `${pct}%` }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function fmtTime(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}
